/**
 * Générateur — Comparer et encadrer des fractions
 * Attendu ID : 628 | Niveau : 6ème | Domaine : NC (Nombres et calculs)
 *
 * Ce générateur contient 11 EXERCICES DIDACTIQUEMENT DISTINCTS (chacun avec
 * son propre generatorKey au niveau exercice), déclinés en plusieurs formats UI.
 *
 * Exercices couverts (voir FICHE_PEDAGO_CLUSTER16 §6.1 à §6.11) :
 *
 *   1. `compare_meme_denominateur`   → QCM unique. Couple (a/b, c/b), 3 options.
 *   2. `compare_meme_numerateur`     → QCM unique. Couple (a/b, a/c), 3 options.
 *   3. `classer_rapport_1`           → Classer. 6 fractions en 3 colonnes <1/=1/>1.
 *   4. `classer_rapport_demi`        → Classer. 6 fractions en 3 colonnes <1/2 / =1/2 / >1/2.
 *   5. `compare_via_1`               → QCM unique. Une fraction <1 et une >1.
 *   6. `compare_via_demi`            → QCM unique. Une fraction <1/2 et une >1/2, dén et num différents.
 *   7. `completer_inegalite`         → Saisie libre (< ou >). Stratégie tirée au hasard.
 *   8. `encadrer_entiers`            → QCM unique. 4 couples d'entiers consécutifs candidats.
 *   9. `nombre_mixte_encadrer`       → Saisie. Écrit fraction impropre en nombre mixte puis encadre.
 *  10. `compare_fraction_entier`     → QCM unique. Compare a/b à un entier m proche.
 *  11. `compare_vrai_faux`           → Tableau V/F. 6 inégalités mélangeant les 4 stratégies.
 *
 * Règles dures de la fiche (§2, §3, §4) :
 *   - Pool_A : couples de même dénominateur, b ∈ [2, 12], numérateurs ≠
 *   - Pool_B : couples de même numérateur, a ∈ [1, 7], |b − c| ≥ 2
 *   - Pool_C_LT1 / EQ1 / GT1 : fractions pour classement à 1
 *   - Pool_D_LT2 / EQ2 / GT2 : fractions pour classement à 1/2
 *   - Pool_E : fractions impropres avec quotient entier ∈ [1, 4]
 *
 * Distracteurs contextualisés (fiche §4) :
 *   1. Ordre inversé même dén
 *   2. Ordre à l'endroit en même numérateur (piège « plus grand dén = plus grand »)
 *   3. Additif résiduel (erreur a+k/b+k)
 *   4. Classement à 1 inversé
 *   5. Critère 1/2 remplacé par critère 1
 *   6. Encadrement trop large ou mal placé
 *   7. Confusion reste ↔ partie fractionnaire
 *   8. Confondre fraction et numérateur
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeClasser,
} from '../../../utils';
import { gcd, fmtFrac, fmtKatex, fmtItem, fmtItemBrut } from './fraction';

// ═══════════════════════════════════════════════════════════
// CONSTANTES & LABELS
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 628;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CMP_MEME_DEN   = 'Comparer deux fractions de même dénominateur';
const L_CMP_MEME_NUM   = 'Comparer deux fractions de même numérateur';
const L_CLS_1          = 'Classer des fractions par rapport à 1';
const L_CLS_DEMI       = 'Classer des fractions par rapport à 1/2';
const L_CMP_VIA_1      = 'Comparer deux fractions en passant par 1';
const L_CMP_VIA_DEMI   = 'Comparer deux fractions en passant par 1/2';
const L_COMPL_INEG     = 'Compléter une inégalité par < ou >';
const L_ENC_ENT_QCM    = 'Encadrer une fraction par deux entiers consécutifs (QCM)';
const L_ENC_ENT_SAI    = 'Encadrer une fraction par deux entiers consécutifs (Saisie)';
const L_MIXTE_ENC      = 'Écrire une fraction en nombre mixte puis encadrer';
const L_CMP_FRAC_ENT   = 'Comparer une fraction à un entier';
const L_CMP_VF         = 'Juger Vrai/Faux sur plusieurs inégalités';

// ═══════════════════════════════════════════════════════════
// HELPERS ARITHMÉTIQUES
// ═══════════════════════════════════════════════════════════

const isIrreducible = (a, b) => gcd(a, b) === 1;

/** Compare a/b et c/d en produit croisé. Retourne -1, 0 ou +1. */
const fracCmp = (a, b, c, d) => {
    const lhs = a * d;
    const rhs = b * c;
    return lhs < rhs ? -1 : (lhs > rhs ? 1 : 0);
};

const fracEqual = (a, b, c, d) => a * d === b * c;
const fracLt = (a, b, c, d) => a * d < b * c;
const fracGt = (a, b, c, d) => a * d > b * c;

/** Formate un nombre mixte "n + a/b". */
const fmtMixte = (n, a, b) => `${n} + ${a}/${b}`;

/** Formate un nombre mixte en KaTeX. */
const fmtMixteKatex = (n, a, b) => `${n} + \\dfrac{${a}}{${b}}`;

/** Construit un elements_additionnels avec une expression KaTeX pour l'énoncé. */
const exprElement = (katexStr) => ({
    version: 1,
    elements: [{ type: 'expression', config: { contenu: katexStr } }],
});

/** Construit un elements_additionnels avec plusieurs expressions KaTeX. */
const multiExprElements = (...katexStrs) => ({
    version: 1,
    elements: katexStrs.map(s => ({ type: 'expression', config: { contenu: s } })),
});

// ═══════════════════════════════════════════════════════════
// POOL_A — Couples de fractions de même dénominateur
// ═══════════════════════════════════════════════════════════

/**
 * POOL_A — clé = dénominateur, valeur = liste de numérateurs disponibles.
 * Respecte la fiche §3 sous-famille A.
 */
const POOL_A = {
    2:  [1, 3, 5, 7],
    3:  [1, 2, 4, 5, 7, 8],
    4:  [1, 3, 5, 7, 9, 11],
    5:  [1, 2, 3, 4, 6, 7, 8, 9],
    6:  [1, 5, 7, 11],
    7:  [1, 2, 3, 4, 5, 6, 8, 9, 10],
    8:  [1, 3, 5, 7, 9, 11, 13],
    9:  [1, 2, 4, 5, 7, 8, 10],
    10: [1, 3, 7, 9, 11, 13],
    11: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12],
    12: [1, 5, 7, 11, 13],
};

/** Vérification runtime de POOL_A. */
for (const [b, nums] of Object.entries(POOL_A)) {
    const bn = Number(b);
    if (bn < 2 || bn > 12) throw new Error(`POOL_A: dénominateur ${b} hors [2,12]`);
    if (nums.length < 4) throw new Error(`POOL_A: pas assez de numérateurs pour ${b}`);
    const setNums = new Set(nums);
    if (setNums.size !== nums.length) throw new Error(`POOL_A: doublon dans ${b}`);
}

// ═══════════════════════════════════════════════════════════
// POOL_B — Couples de fractions de même numérateur
// ═══════════════════════════════════════════════════════════

const POOL_B = {
    1: [2, 3, 4, 5, 6, 7, 8, 9, 10, 12],
    2: [3, 5, 7, 9, 11],
    3: [4, 5, 7, 8, 10, 11],
    4: [5, 7, 9, 11, 13],
    5: [6, 7, 8, 9, 11, 12],
    7: [3, 8, 9, 11, 12],
};

for (const [a, dens] of Object.entries(POOL_B)) {
    const an = Number(a);
    if (an < 1 || an > 9) throw new Error(`POOL_B: num ${a} hors [1,9]`);
    if (dens.length < 4) throw new Error(`POOL_B: pas assez de dén pour ${a}`);
}

// ═══════════════════════════════════════════════════════════
// POOL_C — Fractions pour la comparaison à 1
// ═══════════════════════════════════════════════════════════

const POOL_C_LT1 = [
    [1, 2], [1, 3], [2, 3], [1, 4], [3, 4],
    [2, 5], [3, 5], [4, 5], [5, 6], [7, 8], [9, 10], [11, 12],
    [7, 9], [8, 9], [9, 11], [10, 11], [11, 13],
];

const POOL_C_EQ1 = [
    [2, 2], [3, 3], [4, 4], [5, 5], [6, 6], [7, 7], [8, 8], [9, 9], [11, 11],
];

const POOL_C_GT1 = [
    [3, 2], [5, 3], [7, 4], [5, 4], [9, 5], [7, 6], [10, 9], [11, 8], [13, 10],
    [7, 5], [8, 5], [11, 7], [13, 12], [15, 11], [12, 7],
];

for (const [a, b] of POOL_C_LT1) if (a >= b) throw new Error(`POOL_C_LT1 ${a}/${b} >= 1`);
for (const [a, b] of POOL_C_EQ1) if (a !== b) throw new Error(`POOL_C_EQ1 ${a}/${b} ≠ 1`);
for (const [a, b] of POOL_C_GT1) if (a <= b) throw new Error(`POOL_C_GT1 ${a}/${b} <= 1`);

// ═══════════════════════════════════════════════════════════
// POOL_D — Fractions pour la comparaison à 1/2
// ═══════════════════════════════════════════════════════════

const POOL_D_LT12 = [
    [1, 3], [2, 5], [3, 7], [4, 9], [5, 11], [5, 12], [2, 7], [3, 8], [4, 11], [1, 4],
    [3, 10], [5, 13], [4, 13], [3, 11], [2, 9],
];

const POOL_D_EQ12 = [
    [2, 4], [3, 6], [4, 8], [5, 10], [6, 12], [7, 14],
];

const POOL_D_GT12 = [
    [2, 3], [3, 5], [4, 7], [5, 9], [6, 11], [7, 12], [5, 8], [7, 13], [8, 15],
    [7, 11], [9, 17], [6, 11], [8, 13],
];

for (const [a, b] of POOL_D_LT12) if (2 * a >= b) throw new Error(`POOL_D_LT12 ${a}/${b} >= 1/2`);
for (const [a, b] of POOL_D_EQ12) if (2 * a !== b) throw new Error(`POOL_D_EQ12 ${a}/${b} ≠ 1/2`);
for (const [a, b] of POOL_D_GT12) if (2 * a <= b) throw new Error(`POOL_D_GT12 ${a}/${b} <= 1/2`);

// ═══════════════════════════════════════════════════════════
// POOL_E — Fractions impropres à encadrer (quotient ∈ [1,4])
// ═══════════════════════════════════════════════════════════

const POOL_E = [];
for (let b = 2; b <= 12; b++) {
    for (let a = b + 1; a <= 4 * b + (b - 1); a++) {
        if (a % b === 0) continue;                // exclu : c'est un entier
        const q = Math.floor(a / b);
        if (q < 1 || q > 4) continue;
        POOL_E.push([a, b]);
    }
}

if (POOL_E.length < 50) throw new Error(`POOL_E trop petit (${POOL_E.length})`);

// Vérification runtime de POOL_E
for (const [a, b] of POOL_E) {
    if (a <= b) throw new Error(`POOL_E ${a}/${b} non impropre`);
    if (a % b === 0) throw new Error(`POOL_E ${a}/${b} est un entier`);
    const q = Math.floor(a / b);
    if (q < 1 || q > 4) throw new Error(`POOL_E ${a}/${b} quotient hors [1,4]`);
}

// ═══════════════════════════════════════════════════════════
// SÉLECTEURS DE POOLS
// ═══════════════════════════════════════════════════════════

/** Tire un couple (a/b, c/b) depuis POOL_A avec a ≠ c et |a−c| ≤ 10. */
const pickCoupleMemeDen = () => {
    const bChoices = Object.keys(POOL_A).map(Number);
    for (let attempt = 0; attempt < 50; attempt++) {
        const b = randomChoice(bChoices);
        const nums = POOL_A[b];
        if (nums.length < 2) continue;
        const [a, c] = shuffle(nums).slice(0, 2);
        if (a === c) continue;
        if (Math.abs(a - c) > 10) continue;
        return { a, b, c };
    }
    throw new Error('pickCoupleMemeDen: pas de couple trouvé');
};

/** Tire un couple (a/b, a/c) depuis POOL_B avec b ≠ c et |b−c| ≥ 2. */
const pickCoupleMemeNum = () => {
    const aChoices = Object.keys(POOL_B).map(Number);
    for (let attempt = 0; attempt < 50; attempt++) {
        const a = randomChoice(aChoices);
        const dens = POOL_B[a];
        if (dens.length < 2) continue;
        const [b, c] = shuffle(dens).slice(0, 2);
        if (b === c) continue;
        if (Math.abs(b - c) < 2) continue;
        return { a, b, c };
    }
    throw new Error('pickCoupleMemeNum: pas de couple trouvé');
};

/** Tire une fraction dans POOL_E (impropre, quotient ∈ [1,4]). */
const pickPoolE = () => randomChoice(POOL_E);

// ═══════════════════════════════════════════════════════════
// EXERCICE 1 — compare_meme_denominateur
// ═══════════════════════════════════════════════════════════

const genCompareMemeDenominateur = () => {
    const { a, b, c } = pickCoupleMemeDen();
    // a et c sont distincts. L'ordre est donné par comparaison directe.
    const lt = a < c;
    const left = fmtKatex(a, b);
    const right = fmtKatex(c, b);
    const mathExpr = `${left} \\quad ? \\quad ${right}`;
    const texte = `<p>Compare ces deux fractions. Coche la bonne inégalité.</p>{{element:0}}`;
    // Choix : a/b < c/b, a/b > c/b, a/b = c/b
    const opt_lt = `$${fmtKatex(a, b)} < ${fmtKatex(c, b)}$`;
    const opt_gt = `$${fmtKatex(a, b)} > ${fmtKatex(c, b)}$`;
    const opt_eq = `$${fmtKatex(a, b)} = ${fmtKatex(c, b)}$`;
    const choix = [opt_lt, opt_gt, opt_eq];
    const bonne = lt ? opt_lt : opt_gt;
    const payload = makeQcm(choix, bonne, texte, { ...opts(L_CMP_MEME_DEN), elements_additionnels: exprElement(mathExpr) });
    payload._meta.generatorKey = 'compare_meme_denominateur';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 2 — compare_meme_numerateur
// ═══════════════════════════════════════════════════════════

const genCompareMemeNumerateur = () => {
    const { a, b, c } = pickCoupleMemeNum();
    // a/b vs a/c : l'ordre est INVERSE des dénominateurs. b < c ⇒ a/b > a/c.
    const lt = b > c; // a/b < a/c ⇔ b > c
    const left = fmtKatex(a, b);
    const right = fmtKatex(a, c);
    const mathExpr = `${left} \\quad ? \\quad ${right}`;
    const texte = `<p>Compare ces deux fractions. Attention au dénominateur !</p>{{element:0}}`;
    const opt_lt = `$${fmtKatex(a, b)} < ${fmtKatex(a, c)}$`;
    const opt_gt = `$${fmtKatex(a, b)} > ${fmtKatex(a, c)}$`;
    const opt_eq = `$${fmtKatex(a, b)} = ${fmtKatex(a, c)}$`;
    const choix = [opt_lt, opt_gt, opt_eq];
    const bonne = lt ? opt_lt : opt_gt;
    const payload = makeQcm(choix, bonne, texte, { ...opts(L_CMP_MEME_NUM), elements_additionnels: exprElement(mathExpr) });
    payload._meta.generatorKey = 'compare_meme_numerateur';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 3 — classer_rapport_1
// ═══════════════════════════════════════════════════════════

/** n items du pool à RENDUS distincts (les pools peuvent contenir des doublons). */
const prendreDistincts628 = (pool, n) => {
    const items = [];
    const vus = new Set();
    for (const [a, b] of shuffle(pool)) {
        const it = fmtItemBrut(a, b);
        if (!vus.has(it)) {
            vus.add(it);
            items.push(it);
        }
        if (items.length === n) break;
    }
    return items;
};

const genClasserRapport1 = () => {
    // 2 fractions <1, 2 fractions =1, 2 fractions >1 (la fiche demande au moins 2 par classe).
    // fmtItemBrut : 3/3 doit s'afficher « 3/3 » (fmtItem simplifierait en « 1 »
    // → items dupliqués et réponse donnée). Sélection dédupliquée par rendu
    // (certains pools contiennent des doublons).
    const lt = prendreDistincts628(POOL_C_LT1, 2);
    const eq = prendreDistincts628(POOL_C_EQ1, 2);
    const gt = prendreDistincts628(POOL_C_GT1, 2);
    const categories = [
        { name: '$< 1$', items: lt },
        { name: '$= 1$', items: eq },
        { name: '$> 1$', items: gt },
    ];
    const texte = `<p>Range chaque fraction dans la colonne correspondant à sa position par rapport à 1.</p>`;
    const payload = makeClasser(categories, texte, opts(L_CLS_1));
    payload._meta.generatorKey = 'classer_rapport_1';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 4 — classer_rapport_demi
// ═══════════════════════════════════════════════════════════

const genClasserRapportDemi = () => {
    // 2 par classe ; on force au moins une fraction « piège » (5/12 ou équivalent).
    const lt = prendreDistincts628(POOL_D_LT12, 2);
    const eq = prendreDistincts628(POOL_D_EQ12, 2);
    const gt = prendreDistincts628(POOL_D_GT12, 2);
    const categories = [
        { name: '$< \\dfrac{1}{2}$', items: lt },
        { name: '$= \\dfrac{1}{2}$', items: eq },
        { name: '$> \\dfrac{1}{2}$', items: gt },
    ];
    const texte = `<p>Range chaque fraction dans la colonne correspondant à sa position par rapport à {{element:0}}.</p>`;
    const payload = makeClasser(categories, texte, { ...opts(L_CLS_DEMI), elements_additionnels: exprElement('\\dfrac{1}{2}') });
    payload._meta.generatorKey = 'classer_rapport_demi';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 5 — compare_via_1
// ═══════════════════════════════════════════════════════════

const genCompareVia1 = () => {
    // Une fraction <1 et une >1, de num/dén distincts entre eux.
    let f1, f2;
    for (let attempt = 0; attempt < 50; attempt++) {
        f1 = randomChoice(POOL_C_LT1); // [a1, b1]
        f2 = randomChoice(POOL_C_GT1); // [a2, b2]
        if (f1[0] !== f2[0] && f1[1] !== f2[1]) break;
    }
    const [a1, b1] = f1;
    const [a2, b2] = f2;
    // f1 < 1 < f2 donc f1 < f2 — toujours.
    const left = fmtKatex(a1, b1);
    const right = fmtKatex(a2, b2);
    const mathExpr = `${left} \\quad ? \\quad ${right}`;
    const texte = `<p>Compare ces deux fractions en utilisant 1 comme point de repère.</p>{{element:0}}`;
    const opt_lt = `$${fmtKatex(a1, b1)} < ${fmtKatex(a2, b2)}$`;
    const opt_gt = `$${fmtKatex(a1, b1)} > ${fmtKatex(a2, b2)}$`;
    const opt_eq = `$${fmtKatex(a1, b1)} = ${fmtKatex(a2, b2)}$`;
    const choix = [opt_lt, opt_gt, opt_eq];
    const payload = makeQcm(choix, opt_lt, texte, { ...opts(L_CMP_VIA_1), elements_additionnels: exprElement(mathExpr) });
    payload._meta.generatorKey = 'compare_via_1';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 6 — compare_via_demi
// ═══════════════════════════════════════════════════════════

const genCompareViaDemi = () => {
    // Une fraction <1/2 et une >1/2 avec num et dén totalement distincts.
    let f1, f2;
    for (let attempt = 0; attempt < 100; attempt++) {
        f1 = randomChoice(POOL_D_LT12);
        f2 = randomChoice(POOL_D_GT12);
        if (f1[0] !== f2[0] && f1[1] !== f2[1]) break;
    }
    const [a1, b1] = f1;
    const [a2, b2] = f2;
    // f1 < 1/2 < f2 donc f1 < f2
    const left = fmtKatex(a1, b1);
    const right = fmtKatex(a2, b2);
    const mathExpr = `${left} \\quad ? \\quad ${right}`;
    const texte = `<p>Compare ces deux fractions en utilisant {{element:0}} comme point de repère.</p>{{element:1}}`;
    const opt_lt = `$${fmtKatex(a1, b1)} < ${fmtKatex(a2, b2)}$`;
    const opt_gt = `$${fmtKatex(a1, b1)} > ${fmtKatex(a2, b2)}$`;
    const opt_eq = `$${fmtKatex(a1, b1)} = ${fmtKatex(a2, b2)}$`;
    const choix = [opt_lt, opt_gt, opt_eq];
    const payload = makeQcm(choix, opt_lt, texte, { ...opts(L_CMP_VIA_DEMI), elements_additionnels: multiExprElements('\\dfrac{1}{2}', mathExpr) });
    payload._meta.generatorKey = 'compare_via_demi';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 7 — completer_inegalite
// ═══════════════════════════════════════════════════════════

const genCompleterInegalite = () => {
    // Tire une stratégie parmi A (même dén), B (même num), C (via 1), D (via 1/2).
    const strat = randomChoice(['A', 'B', 'C', 'D']);
    let a1, b1, a2, b2;
    if (strat === 'A') {
        const { a, b, c } = pickCoupleMemeDen();
        a1 = a; b1 = b; a2 = c; b2 = b;
    } else if (strat === 'B') {
        const { a, b, c } = pickCoupleMemeNum();
        a1 = a; b1 = b; a2 = a; b2 = c;
    } else if (strat === 'C') {
        for (let attempt = 0; attempt < 50; attempt++) {
            const [x1, y1] = randomChoice(POOL_C_LT1);
            const [x2, y2] = randomChoice(POOL_C_GT1);
            if (x1 !== x2 && y1 !== y2) {
                a1 = x1; b1 = y1; a2 = x2; b2 = y2;
                break;
            }
        }
    } else {
        for (let attempt = 0; attempt < 100; attempt++) {
            const [x1, y1] = randomChoice(POOL_D_LT12);
            const [x2, y2] = randomChoice(POOL_D_GT12);
            if (x1 !== x2 && y1 !== y2) {
                a1 = x1; b1 = y1; a2 = x2; b2 = y2;
                break;
            }
        }
    }
    const cmp = fracCmp(a1, b1, a2, b2);
    if (cmp === 0) throw new Error('completer_inegalite: couple égal, impossible');
    const bonne = cmp < 0 ? '<' : '>';
    const expr = `${fmtKatex(a1, b1)} \\ \\square \\ ${fmtKatex(a2, b2)}`;
    const texte = `<p>Complète par <strong>&lt;</strong> ou <strong>&gt;</strong>.</p>{{element:0}}`;
    const payload = makeSaisie(bonne, texte, { ...opts(L_COMPL_INEG), strict: true, elements_additionnels: exprElement(expr) });
    payload._meta.generatorKey = 'completer_inegalite';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 8 — encadrer_entiers (2 formats : qcm + saisie)
// ═══════════════════════════════════════════════════════════

const genEncadrerEntiersQcm = () => {
    const [a, b] = pickPoolE();
    const n = Math.floor(a / b); // quotient entier
    const fracK = fmtKatex(a, b);
    const bonne = `$${n} < ${fracK} < ${n + 1}$`;
    // Distracteurs : (n-1, n+1) trop large, (n+1, n+2) décalé, (n, n+2) trop large
    const distCandidats = [
        `$${Math.max(0, n - 1)} < ${fracK} < ${n + 1}$`,
        `$${n + 1} < ${fracK} < ${n + 2}$`,
        `$${n} < ${fracK} < ${n + 2}$`,
        `$${Math.max(0, n - 1)} < ${fracK} < ${n}$`,
    ];
    const dists = [...new Set(distCandidats)].filter(s => s !== bonne).slice(0, 3);
    const choix = [bonne, ...dists];
    const texte = `<p>Encadre la fraction {{element:0}} par deux entiers consécutifs.</p>`;
    const payload = makeQcm(choix, bonne, texte, { ...opts(L_ENC_ENT_QCM), elements_additionnels: exprElement(fmtKatex(a, b)) });
    payload._meta.generatorKey = 'encadrer_entiers';
    return payload;
};

const genEncadrerEntiersSaisie = () => {
    const [a, b] = pickPoolE();
    const n = Math.floor(a / b);
    // Réponse attendue sous la forme « n ; n+1 » (on accepte le nombre du bas seul).
    const bonne = `${n};${n + 1}`;
    const texte = `<p>Encadre {{element:0}} par deux entiers consécutifs. Écris les deux entiers séparés par un point-virgule (<em>petit;grand</em>).</p>`;
    const payload = makeSaisie(bonne, texte, { ...opts(L_ENC_ENT_SAI), strict: true, elements_additionnels: exprElement(fmtKatex(a, b)) });
    payload._meta.generatorKey = 'encadrer_entiers';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 9 — nombre_mixte_encadrer
// ═══════════════════════════════════════════════════════════

const genNombreMixteEncadrer = () => {
    const [a, b] = pickPoolE();
    const n = Math.floor(a / b);
    const r = a - n * b;
    // Réponse attendue : n;r;b;n;n+1 (entier, num, dén, entier bas, entier haut)
    const bonne = `${n};${r};${b};${n};${n + 1}`;
    const fracExpr = fmtKatex(a, b);
    const eqExpr = `${fracExpr} = ? + \\dfrac{?}{?} \\quad\\text{et}\\quad ? < ${fracExpr} < ?`;
    const texte = `<p>Écris {{element:0}} sous forme de nombre mixte, puis encadre-la par deux entiers consécutifs. Sépare les cinq valeurs par un point-virgule dans l'ordre : entier ; numérateur ; dénominateur ; entier du bas ; entier du haut.</p>{{element:1}}`;
    const payload = makeSaisie(bonne, texte, { ...opts(L_MIXTE_ENC), strict: true, elements_additionnels: multiExprElements(fracExpr, eqExpr) });
    payload._meta.generatorKey = 'nombre_mixte_encadrer';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 10 — compare_fraction_entier
// ═══════════════════════════════════════════════════════════

const genCompareFractionEntier = () => {
    // Tire (a, b) dans POOL_E et un entier m ∈ {n-1, n, n+1} (n = ⌊a/b⌋).
    // La fraction n'est jamais exactement un entier ici (POOL_E l'exclut),
    // donc la comparaison est stricte si m ≠ n, et (n < a/b < n+1) si m = n.
    const [a, b] = pickPoolE();
    const n = Math.floor(a / b);
    const m = randomChoice([Math.max(1, n - 1), n, n + 1]);
    // Comparaison : a/b vs m ⇔ a vs m*b
    const diff = a - m * b;
    // diff > 0 ⇒ a/b > m ; diff < 0 ⇒ a/b < m ; diff = 0 ⇒ égal (n'arrive pas ici car a%b ≠ 0)
    let bonne;
    const opt_lt = `$${fmtKatex(a, b)} < ${m}$`;
    const opt_gt = `$${fmtKatex(a, b)} > ${m}$`;
    const opt_eq = `$${fmtKatex(a, b)} = ${m}$`;
    if (diff > 0) bonne = opt_gt;
    else if (diff < 0) bonne = opt_lt;
    else bonne = opt_eq;
    const choix = [opt_lt, opt_gt, opt_eq];
    const mathExpr = `${fmtKatex(a, b)} \\quad ? \\quad ${m}`;
    const texte = `<p>Compare la fraction à l'entier.</p>{{element:0}}`;
    const payload = makeQcm(choix, bonne, texte, { ...opts(L_CMP_FRAC_ENT), elements_additionnels: exprElement(mathExpr) });
    payload._meta.generatorKey = 'compare_fraction_entier';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 11 — compare_vrai_faux
// ═══════════════════════════════════════════════════════════

/**
 * Construit une ligne V/F avec son verdict booléen (true = vrai).
 */
const buildVfItems = () => {
    const items = [];
    // Item 1 : couple même dén, inégalité VRAIE
    {
        const { a, b, c } = pickCoupleMemeDen();
        const lt = a < c;
        const op = randomChoice(['<', '>']);
        const ligne = `$${fmtKatex(a, b)} ${op} ${fmtKatex(c, b)}$`;
        const vrai = (op === '<' && lt) || (op === '>' && !lt);
        items.push({ ligne, vrai });
    }
    // Item 2 : couple même num, inégalité dont la véracité dépend de l'opérateur
    {
        const { a, b, c } = pickCoupleMemeNum();
        // a/b vs a/c : ordre inverse des dén
        const lt = b > c; // a/b < a/c si b > c
        const op = randomChoice(['<', '>']);
        const ligne = `$${fmtKatex(a, b)} ${op} ${fmtKatex(a, c)}$`;
        const vrai = (op === '<' && lt) || (op === '>' && !lt);
        items.push({ ligne, vrai });
    }
    // Item 3 : comparaison à 1 — VRAIE
    {
        const [a, b] = randomChoice(POOL_C_GT1);
        items.push({ ligne: `$${fmtKatex(a, b)} > 1$`, vrai: true });
    }
    // Item 4 : comparaison à 1 — potentiellement fausse
    {
        const [a, b] = randomChoice(POOL_C_LT1);
        const op = randomChoice(['<', '>']);
        const vrai = op === '<'; // a/b < 1 est vrai
        items.push({ ligne: `$${fmtKatex(a, b)} ${op} 1$`, vrai });
    }
    // Item 5 : comparaison à 1/2 — VRAIE ou FAUSSE aléatoire
    {
        const pool = randomChoice([POOL_D_LT12, POOL_D_GT12]);
        const [a, b] = randomChoice(pool);
        const lt12 = 2 * a < b;
        const op = randomChoice(['<', '>']);
        const vrai = (op === '<' && lt12) || (op === '>' && !lt12);
        items.push({ ligne: `$${fmtKatex(a, b)} ${op} \\dfrac{1}{2}$`, vrai });
    }
    // Item 6 : couple même dén, opérateur aléatoire
    {
        const { a, b, c } = pickCoupleMemeDen();
        const lt = a < c;
        const op = randomChoice(['<', '>']);
        const ligne = `$${fmtKatex(a, b)} ${op} ${fmtKatex(c, b)}$`;
        const vrai = (op === '<' && lt) || (op === '>' && !lt);
        items.push({ ligne, vrai });
    }
    return items;
};

const genCompareVraiFaux = () => {
    // On garantit au moins 2 vrais et 2 faux.
    let items;
    for (let attempt = 0; attempt < 30; attempt++) {
        items = buildVfItems();
        const nbVrais = items.filter(x => x.vrai).length;
        if (nbVrais >= 2 && nbVrais <= 4) break;
    }
    // Mélanger les items
    items = shuffle(items);
    const lignes = items.map(x => x.ligne);
    const bonnesReponses = items.map(x => x.vrai ? 0 : 1); // 0 = Vrai, 1 = Faux
    const texte = `<p>Pour chaque ligne, indique si l'inégalité est <strong>vraie</strong> ou <strong>fausse</strong>.</p>`;
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_CMP_VF));
    payload._meta.generatorKey = 'compare_vrai_faux';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    compare_meme_denominateur_qcm_unique: genCompareMemeDenominateur,
    compare_meme_numerateur_qcm_unique:   genCompareMemeNumerateur,
    classer_rapport_1_classer:            genClasserRapport1,
    classer_rapport_demi_classer:         genClasserRapportDemi,
    compare_via_1_qcm_unique:             genCompareVia1,
    compare_via_demi_qcm_unique:          genCompareViaDemi,
    completer_inegalite_texte:            genCompleterInegalite,
    encadrer_entiers_qcm_unique:          genEncadrerEntiersQcm,
    encadrer_entiers_texte:               genEncadrerEntiersSaisie,
    nombre_mixte_encadrer_texte:          genNombreMixteEncadrer,
    compare_fraction_entier_qcm_unique:   genCompareFractionEntier,
    compare_vrai_faux_tableau_vf:         genCompareVraiFaux,
};

// ═══════════════════════════════════════════════════════════
// CATEGORIES (pour l'UI prof)
// ═══════════════════════════════════════════════════════════

const FORMAT_LABELS = {
    qcm_unique: 'QCM',
    qcm_multiple: 'QCM Multi',
    texte: 'Saisie',
    texte_trou_drag: 'Drag',
    texte_trou_saisie: 'Saisie trous',
    relier: 'Relier',
    tableau_vf: 'Vrai/Faux',
    classer: 'Classer',
    ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS)
        .filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'compare_meme_denominateur', label: 'Comparer : même dénominateur', formatsList: buildFormats('compare_meme_denominateur') },
    { id: 'compare_meme_numerateur',   label: 'Comparer : même numérateur',   formatsList: buildFormats('compare_meme_numerateur') },
    { id: 'classer_rapport_1',         label: 'Classer par rapport à 1',      formatsList: buildFormats('classer_rapport_1') },
    { id: 'classer_rapport_demi',      label: 'Classer par rapport à 1/2',    formatsList: buildFormats('classer_rapport_demi') },
    { id: 'compare_via_1',             label: 'Comparer en passant par 1',    formatsList: buildFormats('compare_via_1') },
    { id: 'compare_via_demi',          label: 'Comparer en passant par 1/2',  formatsList: buildFormats('compare_via_demi') },
    { id: 'completer_inegalite',       label: 'Compléter par < ou >',         formatsList: buildFormats('completer_inegalite') },
    { id: 'encadrer_entiers',          label: 'Encadrer par deux entiers',    formatsList: buildFormats('encadrer_entiers') },
    { id: 'nombre_mixte_encadrer',     label: 'Nombre mixte et encadrement',  formatsList: buildFormats('nombre_mixte_encadrer') },
    { id: 'compare_fraction_entier',   label: 'Comparer fraction et entier',  formatsList: buildFormats('compare_fraction_entier') },
    { id: 'compare_vrai_faux',         label: 'Vrai ou faux : inégalités',    formatsList: buildFormats('compare_vrai_faux') },
];

// ═══════════════════════════════════════════════════════════
// ENTRÉE PRINCIPALE
// ═══════════════════════════════════════════════════════════

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // Toujours forcer la clé complète (les générateurs ne posent que le préfixe
    // catégorie ce qui cassait le "regenerate dans le même format")
    payload._meta.generatorKey = key;
    return payload;
};

// Exports internes pour les tests
export const _internals = {
    POOL_A, POOL_B,
    POOL_C_LT1, POOL_C_EQ1, POOL_C_GT1,
    POOL_D_LT12, POOL_D_EQ12, POOL_D_GT12,
    POOL_E,
    GENERATORS, CATEGORIES,
    gcd, isIrreducible, fracCmp, fracEqual, fracLt, fracGt,
    fmtFrac, fmtKatex, fmtMixte, fmtMixteKatex,
    pickCoupleMemeDen, pickCoupleMemeNum, pickPoolE,
};
